/**
 * DG Basics — "Básico do dia" (sections/dg-social.liquid)
 * Flutuação lenta e independente de cada card + reação sutil ao cursor (desktop).
 * Os limites dos cards são garantidos pelo CSS (clamp com margem que já inclui a amplitude);
 * aqui o deslocamento nunca passa dessa amplitude. Para quando a seção sai da tela, quando o
 * visitante pausa e com movimento reduzido. Vídeos: só tocam visíveis; no celular, um por vez.
 * Não intercepta rolagem nem gestos (listeners passivos, sem preventDefault).
 */

const reduceQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
const mobileQuery = window.matchMedia('(max-width: 749px)');
const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
const isPreview = () => ['127.0.0.1', 'localhost'].includes(location.hostname) || Boolean(window.Shopify?.designMode);

class DgSocial {
  constructor(root) {
    this.root = root;
    this.stage = root.querySelector('.dg-soc__stage');
    this.toggle = root.querySelector('.dg-soc__toggle');
    this.visible = false;
    this.paused = false;
    this.pointer = { x: 0, y: 0, tx: 0, ty: 0 };

    // Editor e preview local: mostra os cards ainda sem mídia (placeholders) para conferir a composição
    if (isPreview()) root.querySelectorAll('[data-dg-preview-only]').forEach((el) => (el.hidden = false));

    this.cards = [...root.querySelectorAll('.dg-soc__card')].map((card, i) => ({
      card,
      float: card.querySelector('.dg-soc__float'),
      depth: Number(card.dataset.depth) || 2,
      base: parseFloat(getComputedStyle(card).getPropertyValue('--rot')) || 0,
      // parâmetros independentes e estáveis por card
      px: 7 + ((i * 1.7) % 4),
      py: 6 + ((i * 2.3) % 3.5),
      pr: 9 + ((i * 1.3) % 4),
      fx: i * 1.9,
      fy: i * 0.7 + 1,
      fr: i * 1.1 + 2,
    }));
    this.videos = [...root.querySelectorAll('video')];

    if (!reduceQuery.matches && this.toggle) {
      this.toggle.hidden = false;
      this.toggle.addEventListener('click', () => this.setPaused(!this.paused));
    }

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(
        (entries) => {
          this.visible = entries.some((entry) => entry.isIntersecting);
          this.update();
        },
        { threshold: 0.05 }
      ).observe(this.stage);
    }

    if (finePointer.matches && root.dataset.parallax === 'true') {
      window.addEventListener(
        'pointermove',
        (event) => {
          if (!this.visible) return;
          const r = this.stage.getBoundingClientRect();
          this.pointer.tx = Math.max(-1, Math.min(1, (event.clientX - (r.left + r.width / 2)) / (r.width / 2)));
          this.pointer.ty = Math.max(-1, Math.min(1, (event.clientY - (r.top + r.height / 2)) / (r.height / 2)));
        },
        { passive: true }
      );
    }

    mobileQuery.addEventListener?.('change', () => this.update());
    document.addEventListener('visibilitychange', () => this.update());
  }

  setPaused(paused) {
    this.paused = paused;
    this.root.classList.toggle('is-paused', paused);
    this.toggle.setAttribute('aria-pressed', String(paused));
    this.toggle.setAttribute('aria-label', paused ? this.toggle.dataset.labelPlay : this.toggle.dataset.labelPause);
    this.update();
  }

  active() {
    return this.visible && !this.paused && !reduceQuery.matches && !document.hidden;
  }

  update() {
    this.updateVideos();
    if (this.active()) {
      if (!this.frame) {
        this.start = this.start ?? performance.now();
        this.frame = requestAnimationFrame((t) => this.tick(t));
      }
    } else if (this.frame) {
      cancelAnimationFrame(this.frame);
      this.frame = null;
    }
  }

  updateVideos() {
    const canPlay = this.visible && !this.paused && !reduceQuery.matches && !document.hidden;
    // Só vídeos de cards exibidos; no celular, no máximo um tocando por vez
    const shown = this.videos.filter((v) => v.closest('.dg-soc__card')?.offsetParent !== null);
    const allowed = mobileQuery.matches ? shown.slice(0, 1) : shown;
    this.videos.forEach((video) => {
      if (canPlay && allowed.includes(video)) {
        if (video.preload === 'none') video.preload = 'metadata';
        video.play().catch(() => {});
      } else {
        video.pause();
      }
    });
  }

  tick(now) {
    const t = (now - this.start) / 1000;
    const mobile = mobileQuery.matches;
    const amp = Number(mobile ? this.root.dataset.ampM : this.root.dataset.ampD) || 0;
    const rot = Number(mobile ? this.root.dataset.rotM : this.root.dataset.rotD) || 0;
    const TAU = Math.PI * 2;

    // cursor suavizado (só no desktop)
    this.pointer.x += (this.pointer.tx - this.pointer.x) * 0.06;
    this.pointer.y += (this.pointer.ty - this.pointer.y) * 0.06;
    const parallax = mobile ? 0 : amp * 0.5; // a margem do CSS reserva 1,5× a amplitude

    for (const c of this.cards) {
      const dx = Math.sin((TAU * t) / c.px + c.fx) * amp * 0.7 + this.pointer.x * parallax * (c.depth / 3);
      const dy = Math.sin((TAU * t) / c.py + c.fy) * amp + this.pointer.y * parallax * (c.depth / 3);
      const dr = Math.sin((TAU * t) / c.pr + c.fr) * rot;
      c.float.style.transform = `translate3d(${dx.toFixed(2)}px, ${dy.toFixed(2)}px, 0) rotate(${(c.base + dr).toFixed(2)}deg)`;
    }
    this.frame = requestAnimationFrame((time) => this.tick(time));
  }
}

const init = (scope = document) =>
  scope.querySelectorAll('.dg-soc').forEach((root) => (root.dgSocial = new DgSocial(root)));

init();
document.addEventListener('shopify:section:load', (event) => init(event.target));
